import React, { useState } from 'react';
import { Button, Modal, Row, Col, Card, Spinner } from 'react-bootstrap';
import { FiDownload, FiUser, FiX, FiCreditCard, FiCalendar, FiAlertCircle, FiPackage, FiTrendingUp, FiDollarSign } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

const IndividualReportDownload = ({
    customer,
    productCache,
    bankAccountCache,
    formatCurrency,
    getProductName,
    getBankAccountNumber
}) => {
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const formatDueDate = (paymentDate) => {
        if (!paymentDate) return "—";
        const date = moment(paymentDate).tz("Asia/Singapore");
        const day = date.date();
        const ordinalSuffix = getOrdinalSuffix(day);
        return `${day}${ordinalSuffix} ${date.format("MMM YYYY")}`;
    };

    const calculateOverdueDays = (paymentDate, status, reportMonth) => {
        if (!paymentDate || status === "Paid") return null;

        const paymentDay = moment(paymentDate).tz("Asia/Singapore").date();
        const today = moment().tz("Asia/Singapore").startOf('day');
        const reportDate = moment(reportMonth, 'MMM YYYY').tz("Asia/Singapore");

        const lastDayOfMonth = reportDate.endOf('month').date();
        const actualDay = Math.min(paymentDay, lastDayOfMonth);
        const dueDate = reportDate.date(actualDay).startOf('day');

        if (today.isAfter(dueDate)) {
            return today.diff(dueDate, 'days');
        }
        return null;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Paid": return "#16a34a";
            case "Pending": return "#d97706";
            case "Unpaid": return "#dc2626";
            default: return "#6b7280";
        }
    };

    const getStatusBadge = (status) => {
        const color = getStatusColor(status);
        return {
            backgroundColor: color,
            color: '#fff',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '500'
        };
    };

    const getOverdueBadge = (overdueDays) => {
        if (!overdueDays) return null;

        return {
            backgroundColor: '#dc2626',
            color: '#fff',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '500',
            marginLeft: '8px'
        };
    };

    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ] : [0, 0, 0];
    };

    const calculateEMIDetails = (row) => {
        const billAmount = parseFloat(row.bill_amount) || 0;
        const downPayment = parseFloat(row.down_pay) || 0;
        const emiPlan = parseInt(row.emi_plan_duration) || 1;
        const reports = row.reports || [];
        const purchaseDate = moment(row.timestamp).tz("Asia/Singapore").startOf('day');

        let monthlyEMI, paidAmount = 0, actualPaidMonths = 0;
        let hasDownPaymentPaid = false;

        if (!downPayment || downPayment === 0) {
            monthlyEMI = billAmount / emiPlan;
            paidAmount = reports.filter(r => r.status === "Paid").length * monthlyEMI;
            actualPaidMonths = reports.filter(r => r.status === "Paid").length;
        } else {
            const remainingAmount = billAmount - downPayment;
            monthlyEMI = remainingAmount / (emiPlan - 1);

            const paidReports = reports.filter(r => r.status === "Paid");
            actualPaidMonths = paidReports.length;

            if (actualPaidMonths > 0) {
                const sortedReports = reports.sort((a, b) => moment(a.month, 'MMM YYYY') - moment(b.month, 'MMM YYYY'));
                const firstReport = sortedReports[0];
                hasDownPaymentPaid = firstReport?.status === "Paid";

                if (hasDownPaymentPaid) {
                    paidAmount = downPayment + ((actualPaidMonths - 1) * monthlyEMI);
                } else {
                    paidAmount = downPayment + (actualPaidMonths * monthlyEMI);
                }
            } else {
                paidAmount = downPayment;
                actualPaidMonths = 1;
            }
        }

        const pendingAmount = billAmount - paidAmount;

        const today = moment().tz("Asia/Singapore").startOf('day');
        const monthsSincePurchase = today.diff(purchaseDate, 'months');
        const currentEMIMonth = Math.min(monthsSincePurchase + 1, emiPlan);

        let currentMonthStatus = "Not Started";
        let overdueDays = null;

        if (currentEMIMonth <= emiPlan) {
            const emiMonthStart = moment(purchaseDate).add(currentEMIMonth - 1, 'months');
            const emiMonthName = emiMonthStart.format("MMM YYYY");

            const currentMonthReport = reports.find(r => r.month === emiMonthName);
            currentMonthStatus = currentMonthReport?.status || "Pending";

            if (currentMonthStatus === "Pending" || currentMonthStatus === "Unpaid") {
                const paymentDay = moment(row.payment_date).tz("Asia/Singapore").date();
                let dueDate = emiMonthStart.clone();
                const lastDayOfMonth = dueDate.endOf('month').date();
                const actualDay = Math.min(paymentDay, lastDayOfMonth);
                dueDate = dueDate.date(actualDay).startOf('day');

                if (dueDate.isBefore(emiMonthStart, 'day')) {
                    dueDate = emiMonthStart.add(1, 'month').date(actualDay);
                }

                if (today.isAfter(dueDate, 'day')) {
                    overdueDays = today.diff(dueDate, 'days');
                }
            }
        } else {
            currentMonthStatus = "Completed";
        }

        return {
            monthlyEMI,
            paidAmount,
            pendingAmount,
            paidMonths: actualPaidMonths,
            totalMonths: emiPlan,
            hasDownPayment: downPayment > 0,
            downPaymentAmount: downPayment,
            hasDownPaymentPaid,
            progressPercentage: emiPlan > 0 ? (actualPaidMonths / emiPlan) * 100 : 0,
            remainingMonths: emiPlan - actualPaidMonths,
            overdueDays,
            isOverdue: overdueDays !== null,
            currentMonthStatus
        };
    };

    class PDFGenerator {
        constructor(customer, productCache, getBankAccountNumber, formatCurrency) {
            this.doc = new jsPDF();
            this.customer = customer;
            this.productData = productCache[customer.product_id] || {};
            this.bankAccount = getBankAccountNumber(customer.boDDARefNo);
            this.emiDetails = calculateEMIDetails(customer);
            this.formatCurrency = formatCurrency;
            this.currentDate = moment().tz("Asia/Singapore").format('DD MMM YYYY [at] hh:mm A');

            this.colors = {
                primary: [44, 62, 80],
                secondary: [128, 128, 128],
                success: [22, 163, 74],
                warning: [217, 119, 6],
                danger: [220, 38, 38],
                light: [248, 250, 252]
            };
        }

        setupDocument() {
            this.doc.setFontSize(20);
            this.doc.setTextColor(0, 0, 0);
            this.doc.setFont(undefined, 'bold');
            this.doc.text('EMI PAYMENT REPORT', 105, 20, { align: 'center' });

            this.doc.setFontSize(10);
            this.doc.setTextColor(100, 100, 100);
            this.doc.setFont(undefined, 'normal');
            this.doc.text(`Generated on ${this.currentDate}`, 105, 27, { align: 'center' });

            this.doc.setFontSize(14);
            this.doc.setTextColor(0, 0, 0);
            this.doc.setFont(undefined, 'bold');
            this.doc.text(this.customer.full_name, 105, 35, { align: 'center' });

            if (this.emiDetails.isOverdue) {
                this.doc.setFontSize(8);
                this.doc.setTextColor(220, 38, 38);
                this.doc.setFont(undefined, 'bold');
                this.doc.text(`OVERDUE: ${this.emiDetails.overdueDays} day${this.emiDetails.overdueDays !== 1 ? 's' : ''}`, 105, 42, { align: 'center' });
                this.doc.setFontSize(14);
            }

            this.doc.setDrawColor(200, 200, 200);
            this.doc.line(14, this.emiDetails.isOverdue ? 48 : 42, 196, this.emiDetails.isOverdue ? 48 : 42);

            return this.emiDetails.isOverdue ? 55 : 50;
        }

        addSection(title, yPosition) {
            this.doc.setFontSize(12);
            this.doc.setTextColor(...this.colors.primary);
            this.doc.setFont(undefined, 'bold');
            this.doc.text(title.toUpperCase(), 14, yPosition);

            this.doc.setDrawColor(...this.colors.primary);
            this.doc.line(14, yPosition + 1, 50, yPosition + 1);

            this.doc.setFont(undefined, 'normal');
            return yPosition + 8;
        }

        createModernTable(data, yPosition, options = {}) {
            const {
                headers = [],
                columnStyles = {},
                headStyles = {},
                theme = 'grid',
                spaceAfter = 15,
                margin = { left: 14, right: 14 }
            } = options;

            const tableOptions = {
                startY: yPosition,
                head: headers,
                body: data,
                theme: theme,
                styles: {
                    fontSize: 10,
                    cellPadding: 1.5,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.25,
                    lineHeight: 1.1
                },
                headStyles: {
                    fillColor: this.colors.primary,
                    textColor: 255,
                    fontSize: 8,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 1.5,
                    ...headStyles
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: [51, 51, 51],
                    cellPadding: 1.5,
                    lineHeight: 1.1
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252]
                },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 'auto' },
                    ...columnStyles
                },
                margin: margin,
                tableWidth: 'wrap'
            };

            autoTable(this.doc, tableOptions);
            return this.doc.lastAutoTable.finalY + spaceAfter;
        }

        addCustomerSection(yPosition) {
            yPosition = this.addSection('Customer Information', yPosition);

            const overdueText = this.emiDetails.isOverdue ?
                `${this.emiDetails.overdueDays} day${this.emiDetails.overdueDays !== 1 ? 's' : ''} overdue` :
                '—';

            const customerData = [
                ['Full Name:', this.customer.full_name || '—'],
                ['DDA Reference No:', this.customer.boDDARefNo || '—'],
                ['Bank Account:', this.bankAccount !== "—" ? this.bankAccount : 'Not Available'],
                ['Bank Code:', this.customer.applicantBankCode || '—'],
                ['Payment Due Date:', formatDueDate(this.customer.payment_date)],
                ['Overdue Status:', overdueText]
            ];

            return this.createModernTable(customerData, yPosition, {
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 40 },
                    1: { cellWidth: 60 }
                },
                spaceAfter: 12,
                didDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 1 && data.row.index === 5 && this.emiDetails.isOverdue) {
                        this.doc.setTextColor(220, 38, 38);
                        this.doc.setFont(undefined, 'bold');
                        this.doc.text(data.cell.raw, data.cell.x + 2, data.cell.y + data.cell.height / 2 + 1);
                        this.doc.setTextColor(51, 51, 51);
                        this.doc.setFont(undefined, 'normal');
                    }
                }
            });
        }

        addProductSection(yPosition) {
            yPosition = this.addSection('Product Information', yPosition);

            const productData = [
                ['Product Name:', this.productData.product_name || '—'],
                ['Color:', this.productData.color || '—'],
                ['Capacity:', this.productData.capacity || '—'],
                ['Display:', this.productData.display || '—'],
                ['Size & Weight:', this.productData.size_and_weight || '—'],
                ['Original Price:', this.formatCurrency(this.productData.price)],
            ];

            return this.createModernTable(productData, yPosition, {
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 35 },
                    1: { cellWidth: 65 }
                },
                spaceAfter: 12
            });
        }

        addPaymentHistorySection(yPosition) {
            if (!this.customer.reports || this.customer.reports.length === 0) return yPosition;

            yPosition = this.addSection('Payment History', yPosition);

            const paymentData = this.customer.reports
                .sort((a, b) => moment(a.month, 'MMM YYYY') - moment(b.month, 'MMM YYYY'))
                .map((report, index) => {
                    let amount = this.emiDetails.monthlyEMI;

                    if (this.emiDetails.hasDownPayment) {
                        if (index === 0) {
                            amount = this.emiDetails.downPaymentAmount;
                        } else {
                            amount = this.emiDetails.monthlyEMI;
                        }
                    }

                    const reportOverdue = calculateOverdueDays(this.customer.payment_date, report.status, report.month);
                    const overdueText = reportOverdue !== null ? `${reportOverdue} days` : '—';

                    const currentMonth = moment(report.month, 'MMM YYYY');
                    const nextMonth = moment(currentMonth).add(1, 'month');

                    const month1 = currentMonth.format("MMM");
                    const month2 = nextMonth.format("MMM");
                    const year1 = currentMonth.format("YYYY");
                    const year2 = nextMonth.format("YYYY");

                    const periodText = year1 === year2
                        ? `${month1}-${month2} ${year1}`
                        : `${month1} ${year1}-${month2} ${year2}`;

                    return [
                        periodText,
                        report.status,
                        this.formatCurrency(amount),
                        overdueText,
                        report.comments || '—',
                        moment(report.updated_time).isValid()
                            ? moment(report.updated_time).format('DD MMM YYYY HH:mm')
                            : '—'
                    ];
                });

            return this.createModernTable(paymentData, yPosition, {
                headers: [['Payment Period', 'Status', 'Amount', 'Overdue', 'Comments', 'Updated On']],
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 16, halign: 'center' },
                    2: { cellWidth: 20, halign: 'right' },
                    3: { cellWidth: 16, halign: 'center' },
                    4: { cellWidth: 45 },
                    5: { cellWidth: 28 }
                },
                spaceAfter: 12,
                didDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 1) {
                        const status = data.cell.raw;
                        const color = getStatusColor(status);
                        const rgb = hexToRgb(color);

                        this.doc.setFillColor(...rgb);
                        this.doc.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');
                        this.doc.setTextColor(255, 255, 255);
                        this.doc.setFontSize(6);
                        this.doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
                    }

                    if (data.section === 'body' && data.column.index === 3 && data.cell.raw !== '—') {
                        this.doc.setTextColor(220, 38, 38);
                        this.doc.setFont(undefined, 'bold');
                        this.doc.text(data.cell.raw, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
                        this.doc.setTextColor(51, 51, 51);
                        this.doc.setFont(undefined, 'normal');
                    }
                }
            });
        }

        addFinancialSection(yPosition) {
            yPosition = this.addSection('Financial Summary', yPosition);

            const financialData = [];

            financialData.push(['Total Bill Amount:', this.formatCurrency(this.customer.bill_amount)]);

            if (this.emiDetails.hasDownPayment) {
                financialData.push(['Down Payment:', this.formatCurrency(this.emiDetails.downPaymentAmount)]);
                financialData.push(['Remaining Amount:', this.formatCurrency(this.customer.bill_amount - this.emiDetails.downPaymentAmount)]);
            }

            financialData.push(['EMI Plan Duration:', `${this.emiDetails.totalMonths} months`]);

            if (this.emiDetails.hasDownPayment) {
                financialData.push(['Regular EMI Amount:', this.formatCurrency(this.emiDetails.monthlyEMI)]);
                financialData.push(['EMI Months:', `${this.emiDetails.totalMonths - 1} months`]);
            } else {
                financialData.push(['Monthly EMI Amount:', this.formatCurrency(this.emiDetails.monthlyEMI)]);
            }

            financialData.push(['Total Paid Amount:', this.formatCurrency(this.emiDetails.paidAmount)]);
            financialData.push(['Pending Amount:', this.formatCurrency(this.emiDetails.pendingAmount)]);

            financialData.push(['Progress:', `${this.emiDetails.paidMonths}/${this.emiDetails.totalMonths} months (${this.emiDetails.progressPercentage.toFixed(1)}%)`]);

            financialData.push(['Current Status:', this.emiDetails.currentMonthStatus || 'Not Started']);

            if (this.emiDetails.isOverdue) {
                financialData.push(['Overdue Days:', `${this.emiDetails.overdueDays} day${this.emiDetails.overdueDays !== 1 ? 's' : ''}`]);
            }

            return this.createModernTable(financialData, yPosition, {
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 45 },
                    1: { cellWidth: 55 }
                },
                spaceAfter: 12,
                didDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 1 &&
                        this.emiDetails.isOverdue && data.cell.raw.includes('day')) {
                        this.doc.setTextColor(220, 38, 38);
                        this.doc.setFont(undefined, 'bold');
                        this.doc.text(data.cell.raw, data.cell.x + 2, data.cell.y + data.cell.height / 2 + 1);
                        this.doc.setTextColor(51, 51, 51);
                        this.doc.setFont(undefined, 'normal');
                    }

                    if (data.section === 'body' && data.column.index === 1 &&
                        data.cell.raw === this.formatCurrency(this.emiDetails.pendingAmount)) {
                        this.doc.setTextColor(220, 38, 38);
                        this.doc.setFont(undefined, 'bold');
                        this.doc.text(data.cell.raw, data.cell.x + 2, data.cell.y + data.cell.height / 2 + 1);
                        this.doc.setTextColor(51, 51, 51);
                        this.doc.setFont(undefined, 'normal');
                    }

                    if (data.section === 'body' && data.column.index === 1 &&
                        data.cell.raw === this.formatCurrency(this.emiDetails.paidAmount)) {
                        this.doc.setTextColor(22, 163, 74);
                        this.doc.setFont(undefined, 'bold');
                        this.doc.text(data.cell.raw, data.cell.x + 2, data.cell.y + data.cell.height / 2 + 1);
                        this.doc.setTextColor(51, 51, 51);
                        this.doc.setFont(undefined, 'normal');
                    }
                }
            });
        }

        addSummarySection(yPosition) {
            yPosition = this.addSection('Summary', yPosition);

            this.doc.setFontSize(8);
            this.doc.setTextColor(51, 51, 51);

            const summaryPoints = [];

            if (this.emiDetails.hasDownPayment) {
                summaryPoints.push(`• Down payment of ${this.formatCurrency(this.emiDetails.downPaymentAmount)} made at purchase`);
                if (this.emiDetails.hasDownPaymentPaid) {
                    summaryPoints.push(`• Down payment is marked as PAID`);
                }
                summaryPoints.push(`• Regular EMI of ${this.formatCurrency(this.emiDetails.monthlyEMI)} for ${this.emiDetails.totalMonths - 1} months`);
            } else {
                summaryPoints.push(`• No down payment - Standard EMI of ${this.formatCurrency(this.emiDetails.monthlyEMI)}`);
            }

            summaryPoints.push(`• Completed ${this.emiDetails.paidMonths} of ${this.emiDetails.totalMonths} EMI payments`);
            summaryPoints.push(`• ${this.formatCurrency(this.emiDetails.paidAmount)} paid out of ${this.formatCurrency(this.customer.bill_amount)} total`);
            summaryPoints.push(`• ${this.formatCurrency(this.emiDetails.pendingAmount)} remaining to be paid`);
            summaryPoints.push(`• ${this.emiDetails.remainingMonths} payments remaining`);
            summaryPoints.push(`• Current status: ${this.emiDetails.currentMonthStatus || 'Not Started'}`);

            if (this.emiDetails.isOverdue) {
                summaryPoints.push(`• Payment is ${this.emiDetails.overdueDays} day${this.emiDetails.overdueDays !== 1 ? 's' : ''} overdue - ACTION REQUIRED`);
            }

            summaryPoints.forEach((point, index) => {
                if (point.includes('overdue') || point.includes('REQUIRED')) {
                    this.doc.setTextColor(220, 38, 38);
                    this.doc.setFont(undefined, 'bold');
                } else if (point.includes('PAID') || point.includes('Completed')) {
                    this.doc.setTextColor(22, 163, 74);
                }
                this.doc.text(point, 16, yPosition + (index * 4));
                this.doc.setTextColor(51, 51, 51);
                this.doc.setFont(undefined, 'normal');
            });
        }

        addPageNumbers() {
            const pageCount = this.doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                this.doc.setPage(i);
                this.doc.setFontSize(7);
                this.doc.setTextColor(...this.colors.secondary);
                this.doc.text(`Page ${i} of ${pageCount}`, 195, 290, { align: 'right' });
            }
        }

        generate() {
            let yPosition = this.setupDocument();

            yPosition = this.addCustomerSection(yPosition);
            yPosition = this.addProductSection(yPosition);
            yPosition = this.addPaymentHistorySection(yPosition);
            yPosition = this.addFinancialSection(yPosition);

            if (yPosition > 220) {
                this.doc.addPage();
                yPosition = 20;
            }

            if (yPosition < 260) {
                this.addSummarySection(yPosition);
            }

            this.addPageNumbers();

            return this.doc;
        }
    }

    const generateIndividualPDF = () => {
        if (!customer) return;

        setLoading(true);
        try {
            const generator = new PDFGenerator(
                customer,
                productCache,
                getBankAccountNumber,
                formatCurrency
            );

            const doc = generator.generate();
            const fileName = `${customer.full_name.replace(/\s+/g, '_')}_EMI_Report_${moment().format('YYYY-MM-DD')}.pdf`;

            doc.save(fileName);
            toast.success(`Report downloaded for ${customer.full_name}`);
            setShowModal(false);
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF report');
        } finally {
            setLoading(false);
        }
    };

    const emiDetails = calculateEMIDetails(customer);
    const productData = productCache[customer?.product_id] || {};
    const bankAccount = getBankAccountNumber(customer?.boDDARefNo);

    return (
        <>
            <Button
                style={{
                    fontSize: '12px',
                    borderColor: getStatusColor(emiDetails.currentMonthStatus),
                    color: 'white',
                    backgroundColor: getStatusColor(emiDetails.currentMonthStatus),
                }}
                variant="outline-primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => setShowModal(true)}
                disabled={!customer}
            >
                <FiDownload size={12} />
                {customer?.full_name}
            </Button>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                dialogClassName="modal-90w"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header
                    className="position-relative border-0 pb-2"
                    style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 1050,
                        padding: '1rem 1.5rem'
                    }}
                >
                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                        <FiUser />
                        Customer Information
                    </h6>
                    <Button
                        variant="link"
                        className="position-absolute top-0 end-0 p-1 m-1 text-muted"
                        onClick={() => setShowModal(false)}
                        style={{
                            fontSize: '14px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1051
                        }}
                        onFocus={(e) => e.target.style.boxShadow = 'none'}
                    >
                        <FiX size={16} />
                    </Button>
                </Modal.Header>
                <Modal.Body
                    className="p-0"
                    style={{
                        maxHeight: 'calc(100vh - 200px)',
                        overflowY: 'auto'
                    }}
                >
                    {customer && (
                        <div className="p-3" style={{ minHeight: '100%' }}>
                            <div className="space-y-3">
                                <Card className="border-0 bg-light">
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <small className="text-muted">Full Name</small>
                                                    <div className="fw-semibold">{customer.full_name}</div>
                                                </div>
                                                <div className="mb-2">
                                                    <small className="text-muted">DDA Reference</small>
                                                    <div className="fw-semibold">{customer.boDDARefNo || "—"}</div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <small className="text-muted">Bank Account</small>
                                                    <div className="fw-semibold d-flex align-items-center gap-1">
                                                        <FiCreditCard size={14} />
                                                        {bankAccount}
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <small className="text-muted">Payment Due Date</small>
                                                    <div className="fw-semibold d-flex align-items-center gap-1">
                                                        <FiCalendar size={14} />
                                                        {formatDueDate(customer.payment_date)}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                        {emiDetails.isOverdue && (
                                            <Row>
                                                <Col>
                                                    <div className="mt-2 p-2 bg-danger text-white rounded d-flex align-items-center gap-2">
                                                        <FiAlertCircle size={16} />
                                                        <strong>Overdue: {emiDetails.overdueDays} day{emiDetails.overdueDays !== 1 ? 's' : ''}</strong>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>

                                <Card className="border-0 bg-light">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                            <FiPackage />
                                            Product Information
                                        </h6>
                                        <div className="fw-semibold mb-2">
                                            {productData.product_name || "Loading..."}
                                        </div>
                                        {productData.fullData && (
                                            <Row>
                                                <Col md={6}>
                                                    <small className="text-muted">Color: {productData.color}</small>
                                                </Col>
                                                <Col md={6}>
                                                    <small className="text-muted">Capacity: {productData.capacity}</small>
                                                </Col>
                                                <Col md={6}>
                                                    <small className="text-muted">Display: {productData.display}</small>
                                                </Col>
                                                <Col md={6}>
                                                    <small className="text-muted">Price: {formatCurrency(productData.price)}</small>
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>

                                <Card className="border-0 bg-light">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                            <FiTrendingUp />
                                            EMI Financial Summary
                                        </h6>
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <small className="text-muted">Total Bill Amount</small>
                                                    <div className="fw-semibold text-primary">
                                                        {formatCurrency(customer.bill_amount)}
                                                    </div>
                                                </div>
                                                {emiDetails.hasDownPayment && (
                                                    <>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Down Payment</small>
                                                            <div className="fw-semibold">
                                                                {formatCurrency(emiDetails.downPaymentAmount)}
                                                            </div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Remaining Amount</small>
                                                            <div className="fw-semibold">
                                                                {formatCurrency(customer.bill_amount - emiDetails.downPaymentAmount)}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="mb-2">
                                                    <small className="text-muted">EMI Plan Duration</small>
                                                    <div className="fw-semibold">
                                                        {emiDetails.totalMonths} months
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                {emiDetails.hasDownPayment ? (
                                                    <div className="mb-2">
                                                        <small className="text-muted">Regular EMI</small>
                                                        <div className="fw-semibold">
                                                            {formatCurrency(emiDetails.monthlyEMI)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="mb-2">
                                                        <small className="text-muted">Monthly EMI</small>
                                                        <div className="fw-semibold">
                                                            {formatCurrency(emiDetails.monthlyEMI)}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="mb-2">
                                                    <small className="text-muted">Paid Amount</small>
                                                    <div className="fw-semibold text-success">
                                                        {formatCurrency(emiDetails.paidAmount)}
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <small className="text-muted">Pending Amount</small>
                                                    <div className="fw-semibold text-danger">
                                                        {formatCurrency(emiDetails.pendingAmount)}
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <small className="text-muted">Progress</small>
                                                    <div className="fw-semibold">
                                                        {emiDetails.paidMonths}/{emiDetails.totalMonths} months ({emiDetails.progressPercentage.toFixed(1)}%)
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                        <div className="mt-2">
                                            <small className="text-muted">Current Status</small>
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={getStatusBadge(emiDetails.currentMonthStatus)}>
                                                    {emiDetails.currentMonthStatus || "Not Started"}
                                                </span>
                                                {emiDetails.isOverdue && (
                                                    <span style={getOverdueBadge(emiDetails.overdueDays)}>
                                                        {emiDetails.overdueDays} day{emiDetails.overdueDays !== 1 ? 's' : ''} overdue
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                {customer.reports && customer.reports.length > 0 && (
                                    <Card className="border-0 bg-light">
                                        <Card.Body>
                                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                <FiDollarSign />
                                                Payment History
                                            </h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-borderless">
                                                    <thead>
                                                        <tr>
                                                            <th>Payment Period</th>
                                                            <th>Status</th>
                                                            <th>Amount</th>
                                                            <th>Overdue Days</th>
                                                            <th>Comments</th>
                                                            <th>Updated</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {customer.reports
                                                            .sort((a, b) => moment(a.month, 'MMM YYYY') - moment(b.month, 'MMM YYYY'))
                                                            .map((report, index) => {
                                                                let amount = emiDetails.monthlyEMI;

                                                                if (emiDetails.hasDownPayment) {
                                                                    if (index === 0) {
                                                                        amount = emiDetails.downPaymentAmount;
                                                                    } else {
                                                                        amount = emiDetails.monthlyEMI;
                                                                    }
                                                                }

                                                                const reportOverdue = calculateOverdueDays(customer.payment_date, report.status, report.month);
                                                                const overdueText = reportOverdue !== null ? `${reportOverdue} days` : '—';

                                                                // Create payment period display
                                                                const currentMonth = moment(report.month, 'MMM YYYY');
                                                                const nextMonth = moment(currentMonth).add(1, 'month');

                                                                const month1 = currentMonth.format("MMM");
                                                                const month2 = nextMonth.format("MMM");
                                                                const year1 = currentMonth.format("YYYY");
                                                                const year2 = nextMonth.format("YYYY");

                                                                const periodText = year1 === year2
                                                                    ? `${month1}-${month2} ${year1}`
                                                                    : `${month1} ${year1}-${month2} ${year2}`;

                                                                return (
                                                                    <tr key={index}>
                                                                        <td>{periodText}</td>
                                                                        <td>
                                                                            <span style={getStatusBadge(report.status)}>
                                                                                {report.status}
                                                                            </span>
                                                                        </td>
                                                                        <td>{formatCurrency(amount)}</td>
                                                                        <td>
                                                                            {reportOverdue !== null ? (
                                                                                <span style={getOverdueBadge(reportOverdue)}>
                                                                                    {overdueText}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <small className="text-muted">
                                                                                {report.comments || '—'}
                                                                            </small>
                                                                        </td>
                                                                        <td>
                                                                            <small>
                                                                                {report.updated_time ? moment(report.updated_time).format('DD MMM YYYY') : '—'}
                                                                            </small>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer
                    className="border-0"
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'white',
                        zIndex: 1050,
                        padding: '1rem 1.5rem'
                    }}
                >
                    <Button variant="secondary" size="sm" style={{ fontSize: "12px" }} onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={generateIndividualPDF}
                        disabled={loading}
                        style={{ fontSize: "12px" }}
                        className="d-flex align-items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                Generating PDF...
                            </>
                        ) : (
                            <>
                                <FiDownload size={14} />
                                Download PDF Report
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default IndividualReportDownload;